// Minimal, reusable Web Speech API wrapper for live dictation. Capability-gated
// (Chrome/Android); everywhere else `voiceSupported()` is false and callers hide
// the mic — the same progressive-enhancement rule as Web NFC.
//
// Used by <creator-flow> (describe + AI-theme prompt) and <nfc-search>.

/** Append a chunk to a running transcript with single-space joins. */
export function joinText(base: string, chunk: string): string {
  return (base ? base.trimEnd() + ' ' : '') + chunk.trim();
}

/** True where the browser exposes SpeechRecognition (Chrome/Android). */
export function voiceSupported(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as unknown as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown };
  return !!(w.SpeechRecognition || w.webkitSpeechRecognition);
}

export interface DictationOptions {
  /** Called on every recognition update with the newly-finalized chunk (may be
   *  '') and the current interim (not-yet-final) text. */
  onResult: (finalChunk: string, interim: string) => void;
  /** Called once when recognition stops (user stop, silence, or error). */
  onEnd: () => void;
  /** BCP-47 language; defaults to the navigator language. */
  lang?: string;
}

/** One live dictation session. Create once, `start()`/`stop()` as needed. */
export class Dictation {
  private rec: SpeechRecognitionLike | null = null;

  get listening(): boolean { return this.rec !== null; }

  start(opts: DictationOptions): void {
    if (this.rec) return;
    const w = window as unknown as { SpeechRecognition?: SpeechCtor; webkitSpeechRecognition?: SpeechCtor };
    const Ctor = w.SpeechRecognition || w.webkitSpeechRecognition;
    if (!Ctor) return;
    const rec = new Ctor();
    rec.lang = opts.lang || (typeof navigator !== 'undefined' ? navigator.language : '') || 'en-US';
    rec.interimResults = true;
    rec.continuous = true;
    rec.onresult = (e: SpeechRecognitionEventLike) => {
      let finalChunk = '';
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        const text = res[0]?.transcript ?? '';
        if (res.isFinal) finalChunk += text;
        else interim += text;
      }
      opts.onResult(finalChunk, interim);
    };
    rec.onerror = () => this.stop();
    rec.onend = () => { this.rec = null; opts.onEnd(); };
    this.rec = rec;
    try { rec.start(); } catch { this.stop(); }
  }

  stop(): void {
    const rec = this.rec;
    this.rec = null;
    if (rec) { rec.onend = null; try { rec.stop(); } catch { /* already stopped */ } }
  }
}

// Minimal Web Speech API shapes (not in every lib.dom); we only touch what we use.
interface SpeechAlternativeLike { readonly transcript: string; }
interface SpeechResultLike { readonly isFinal: boolean; readonly length: number; [i: number]: SpeechAlternativeLike; }
interface SpeechResultListLike { readonly length: number; [i: number]: SpeechResultLike; }
interface SpeechRecognitionEventLike extends Event { readonly resultIndex: number; readonly results: SpeechResultListLike; }
interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start(): void;
  stop(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
}
type SpeechCtor = new () => SpeechRecognitionLike;
